import React, { useMemo } from 'react';
import { Row, Col, Card, Skeleton } from 'antd';
import { CheckCircleOutlined } from '@ant-design/icons';
import { Typography } from 'antd';
import OnlineIndicator from '../../components/OnlineIndicator';
import StackedAreaChart from '../../components/StackedAreaChart';
import { TOKEN_CHART_SERIES, TOKEN_TIME_SERIES, CLOUD_SPEND_CHART_SERIES, CLOUD_SPEND_TIME_SERIES } from '../../utils/chartTimeSeries';
import { formatMoney } from '../../utils/formatMoney';
import { dashboardStatsColumnCount } from '../../utils/dashboardCloudSpend';
import { useDashboard } from './DashboardContext';
import DashboardStatCard from './DashboardStatCard';
import RecentActivityCard from './RecentActivityCard';
import SystemMetricsCard from './SystemMetricsCard';
import {
  formatPercentageChange,
  formatNumber,
  formatUptime,
  formatSince,
} from './dashboardUtils';

const { Text } = Typography;

export default function DashboardOverview() {
  const {
    isAdmin,
    showCloudSpend,
    statistics,
    loading,
    requestsChart,
    tokenChart,
    cloudSpendChart,
    modelDistribution,
  } = useDashboard();

  const statsColumnCount = useMemo(
    () => dashboardStatsColumnCount({ isAdmin, showCloudSpend }),
    [isAdmin, showCloudSpend],
  );

  const topModels = useMemo(
    () => [...(modelDistribution || [])]
      .sort((a, b) => (b.total_tokens || 0) - (a.total_tokens || 0))
      .slice(0, 5),
    [modelDistribution],
  );

  return (
    <>
      <Row
        gutter={[16, 16]}
        align="top"
        className={`dashboard-stats-row dashboard-stats-row--cols-${statsColumnCount}`}
      >
        <Col xs={24} sm={12} className="dashboard-stat-col">
          <DashboardStatCard title="Active assistants" loading={loading.statistics}>
            <OnlineIndicator
              isOnline={statistics?.assistant_stats?.active_assistants > 0}
              size="md"
              text={`${statistics?.assistant_stats?.active_assistants || 0}/${statistics?.assistant_stats?.total_assistants || 0}`}
              textClassName="stat-value"
            />
            {formatPercentageChange(statistics?.assistant_stats?.active_change_percentage)}
          </DashboardStatCard>
        </Col>

        {isAdmin && (
          <Col xs={24} sm={12} className="dashboard-stat-col">
            <DashboardStatCard title="Total users" loading={loading.statistics}>
              <div className="stat-value">{formatNumber(statistics?.user_stats?.total_users)}</div>
              {formatPercentageChange(statistics?.user_stats?.change_percentage)}
            </DashboardStatCard>
          </Col>
        )}

        <Col xs={24} sm={12} className="dashboard-stat-col">
          <DashboardStatCard title="Token usage" loading={loading.statistics}>
            <div className="stat-value">
              {statistics?.token_usage?.total_tokens ? formatNumber(statistics.token_usage.total_tokens) : 0}
            </div>
            {formatPercentageChange(statistics?.token_usage?.change_percentage, { invertColors: true })}
          </DashboardStatCard>
        </Col>

        {showCloudSpend && (
          <Col xs={24} sm={12} className="dashboard-stat-col">
            <DashboardStatCard title="Cloud spend" loading={loading.statistics}>
              <div className="stat-value">{formatMoney(statistics?.token_usage?.total_cost_usd)}</div>
              {formatPercentageChange(statistics?.token_usage?.cost_change_percentage, { invertColors: true })}
            </DashboardStatCard>
          </Col>
        )}

        {isAdmin && (
          <Col xs={24} sm={12} className="dashboard-stat-col">
            <DashboardStatCard title="Platform uptime" loading={loading.statistics}>
              <div className="stat-value">{formatUptime(statistics?.availability?.uptime_seconds)}</div>
              <div className="dashboard-stat__meta">
                <CheckCircleOutlined style={{ color: 'var(--color-success)', marginRight: 4 }} />
                <Text>Since {formatSince(statistics?.availability?.since)}</Text>
              </div>
            </DashboardStatCard>
          </Col>
        )}
      </Row>

      {topModels.length > 0 && (
        <Row gutter={[16, 16]} style={{ marginBottom: 20 }}>
          <Col xs={24}>
            <Card className="modern-card" title={<span className="card-title">Top models by token consumption</span>}>
              <div style={{ display: 'flex', gap: 24, flexWrap: 'wrap' }}>
                {topModels.map((m) => (
                  <div key={m.model}>
                    <Text strong>{m.model}</Text>
                    <div style={{ fontSize: 13, color: 'var(--color-text-secondary)' }}>
                      {formatNumber(m.total_tokens)} tokens
                      {showCloudSpend && m.total_cost_usd != null ? ` · ${formatMoney(m.total_cost_usd)}` : ''}
                    </div>
                  </div>
                ))}
              </div>
            </Card>
          </Col>
        </Row>
      )}

      <Row gutter={[16, 16]} style={{ marginBottom: 20 }}>
        <Col xs={24} lg={12}>
          <Card className="modern-card" title={<span className="card-title">Requests over time</span>}>
            {loading.requests ? (
              <Skeleton active paragraph={{ rows: 8 }} />
            ) : (
              <StackedAreaChart height={300} data={requestsChart.data} emptyState={requestsChart.emptyState} />
            )}
          </Card>
        </Col>
        <Col xs={24} lg={12}>
          <Card className="modern-card" title={<span className="card-title">Token usage over time</span>}>
            {loading.tokenUsage ? (
              <Skeleton active paragraph={{ rows: 8 }} />
            ) : (
              <StackedAreaChart
                height={300}
                data={tokenChart.data}
                emptyState={tokenChart.emptyState}
                series={TOKEN_CHART_SERIES}
                valueKeys={TOKEN_TIME_SERIES.valueKeys}
                flatLineKey="tokens"
                totalLabel="Total tokens"
              />
            )}
          </Card>
        </Col>
      </Row>

      {showCloudSpend && (
        <Row gutter={[16, 16]} style={{ marginBottom: 20 }}>
          <Col xs={24}>
            <Card className="modern-card" title={<span className="card-title">Cloud spend over time</span>}>
              {loading.cloudSpend ? (
                <Skeleton active paragraph={{ rows: 8 }} />
              ) : (
                <StackedAreaChart
                  height={300}
                  data={cloudSpendChart.data}
                  emptyState={cloudSpendChart.emptyState}
                  series={CLOUD_SPEND_CHART_SERIES}
                  valueKeys={CLOUD_SPEND_TIME_SERIES.valueKeys}
                  flatLineKey="total_cost_usd"
                  totalLabel="Total spend"
                  formatValue={formatMoney}
                  yAxisMin={0.0001}
                />
              )}
            </Card>
          </Col>
        </Row>
      )}

      <Row gutter={[16, 16]}>
        <Col xs={24} lg={isAdmin ? 16 : 24}>
          <RecentActivityCard />
        </Col>
        {isAdmin && (
          <Col xs={24} lg={8}>
            <SystemMetricsCard />
          </Col>
        )}
      </Row>
    </>
  );
}
